<!--
  StoreDepartmentMapping.vue -- Client Portal v2 PR B.

  Build contract:
  docs/planning/2026-06-30-client-portal-v2-data-api-refactor.md section 5.1.

  The store-editor card that maps a store to 0 / 1 / N of its CLIENT's
  departments. Departments are a client-level concept now (decision 5); they
  are authored on the client surface (ClientDepartmentsPanel). This card only
  picks which of them apply to THIS store:

    - 0 selected  -> no department attribution at checkout
    - 1 selected  -> auto-attribute (shopper not asked)
    - N selected  -> shopper must pick at checkout (PR C reads this mapping)

  Self-contained persistence (mirrors StoreDefaultsSection): own GET + PUT
  against /api/stores/<id>/departments/, sourcing candidates from
  /api/clients/<id>/departments/. Not part of the campaign autosave flow.
-->
<template>
  <EditorCard
    title="Departments"
    description="Map this store to the client's departments. Orders placed here are attributed to the matching cost center."
  >
    <div class="store-dept-map" data-testid="store-department-mapping">
      <div v-if="loading" data-testid="store-dept-loading">Loading departments...</div>

      <div
        v-else-if="departments.length === 0"
        class="store-dept-empty"
        data-testid="store-dept-empty"
      >
        This client has no departments yet. Add departments on the client's
        page, then map them here.
      </div>

      <template v-else>
        <ul class="store-dept-list">
          <li
            v-for="dept in departments"
            :key="dept.id"
            class="store-dept-row"
            :data-testid="`store-dept-row-${dept.id}`"
          >
            <v-checkbox
              :model-value="selectedIds.includes(dept.id)"
              :label="dept.name"
              hide-details
              density="comfortable"
              :data-testid="`store-dept-checkbox-${dept.id}`"
              @update:modelValue="toggle(dept.id, $event)"
            />
          </li>
        </ul>

        <div class="store-dept-actions">
          <v-btn
            color="primary"
            rounded="pill"
            :loading="saving"
            data-testid="store-dept-save"
            @click="save"
          >
            Save departments
          </v-btn>
        </div>
      </template>
    </div>
  </EditorCard>
</template>

<script>
import axios from "@/axios";
import EditorCard from "./components/EditorCard.vue";

export default {
  name: "StoreDepartmentMapping",

  components: { EditorCard },

  props: {
    storeId: { type: [Number, String], required: true },
    // The store's client. When absent, no departments can be sourced.
    clientId: { type: [Number, String], default: null },
  },

  data() {
    return {
      loading: true,
      saving: false,
      departments: [],
      selectedIds: [],
    };
  },

  mounted() {
    this.load();
  },

  methods: {
    async load() {
      this.loading = true;
      try {
        if (this.clientId == null) {
          this.departments = [];
          this.selectedIds = [];
          return;
        }
        const [deptsResp, mapResp] = await Promise.all([
          axios.get(`/api/clients/${this.clientId}/departments/`),
          axios.get(`/api/stores/${this.storeId}/departments/`),
        ]);
        // Only ACTIVE departments are selectable mappings.
        const all = (deptsResp.data && deptsResp.data.departments) || [];
        this.departments = all.filter((d) => d.is_active !== false);
        this.selectedIds = (
          (mapResp.data && mapResp.data.client_department_ids) || []
        ).map((i) => Number(i));
      } catch (e) {
        this.$store && this.$store.dispatch
          ? this.$store.dispatch("raiseError", "Failed to load departments")
          : null;
      } finally {
        this.loading = false;
      }
    },

    toggle(id, checked) {
      const has = this.selectedIds.includes(id);
      if (checked && !has) {
        this.selectedIds = [...this.selectedIds, id];
      } else if (!checked && has) {
        this.selectedIds = this.selectedIds.filter((x) => x !== id);
      }
    },

    async save() {
      this.saving = true;
      try {
        const resp = await axios.put(
          `/api/stores/${this.storeId}/departments/`,
          { client_department_ids: this.selectedIds },
        );
        this.selectedIds = (
          (resp.data && resp.data.client_department_ids) || []
        ).map((i) => Number(i));
        this.$emit("saved");
      } catch (e) {
        this.$store && this.$store.dispatch
          ? this.$store.dispatch(
              "raiseError",
              "Failed to save department mapping",
            )
          : null;
      } finally {
        this.saving = false;
      }
    },
  },
};
</script>

<style scoped>
.store-dept-list {
  list-style: none;
  margin: 0 0 1rem 0;
  padding: 0;
}
.store-dept-row {
  padding: 2px 0;
}
.store-dept-empty {
  color: var(--tf-fg-4);
  padding: 0.5rem 0;
}
.store-dept-actions {
  margin-top: 0.5rem;
}
</style>
